/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { autoClamp } from "@eisinc/App/Utilities/helpers";
import { screen } from "@eisinc/XDL/Helpers";
import { xBaseTheme } from "@eisinc/XDL/types";
import { getProp } from "@insite/mobius/utilities";
import { css } from "styled-components";

export const fontConfig = {
    url: "https://media.eis-inc.com/m/cffa8027286aa260/original/eis-gibson-font_styles.css",
    weight: {
        regular: 400,
        medium: 500,
    },
    xxSmall: {
        size: 10,
        lineHeight: "14px",
    },
    xSmall: {
        size: 12,
        lineHeight: "17px",
    },
    small: {
        size: 14,
        lineHeight: "22px",
    },
    regular: {
        size: 16,
        lineHeight: "26px",
    },
    headLine: {
        size: 32,
        lineHeight: "38px",
    },
    headLineMobile: {
        size: 28,
        lineHeight: "34px",
    },
    singleLineLarge: {
        size: 20,
        lineHeight: "24px",
    },
    singleLineRegular: {
        size: 16,
        lineHeight: "19px",
    },
    singleLineSmall: {
        size: 14,
        lineHeight: "17px",
    },
    singleLinexSmall: {
        size: 12,
        lineHeight: "14px",
    },
    singleLinexxSmall: {
        size: 10,
        lineHeight: "12px",
    },
    fontFamily: "Gibson",
};

export default {
    fontFamilyImportUrl: fontConfig.url,
    body: {
        size: "16px",
        weight: fontConfig.weight.regular,
        fontFamily: fontConfig.fontFamily,
        color: "text",
        css: css`
            transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
        `,
    },
    p: {
        size: "16px",
        weight: fontConfig.weight.regular,
        css: css`
            margin-bottom: 16px;
            letter-spacing: 0;
            line-height: 26px;
            display: block;
        `,
    },
    h1: {
        size: autoClamp(28, 30, 32),
        weight: fontConfig.weight.medium,
        lineHeight: "38px",
        css: css`
            margin-bottom: 16px;
            letter-spacing: 0;
            display: block;
            ${screen("lte", "md")`
                line-height: 34px;
            `}
        `,
    },
    h2: {
        size: autoClamp(26, 27, 28),
        weight: fontConfig.weight.medium,
        lineHeight: "34px",
        css: css`
            margin-bottom: 16px;
            letter-spacing: 0;
            display: block;
            ${screen("lte", "md")`
                line-height: 31px;
            `}
        `,
    },
    h3: {
        size: "24px",
        weight: fontConfig.weight.medium,
        lineHeight: "29px",
        css: css`
            margin-bottom: 12px;
            letter-spacing: 0;
            display: block;
        `,
    },
    h4: {
        size: "20px",
        weight: fontConfig.weight.medium,
        lineHeight: "24px",
        css: css`
            margin-bottom: 8px;
            display: block;
            line-height: 24px;
        `,
    },
    h5: {
        size: "16px",
        weight: fontConfig.weight.medium,
        lineHeight: "19px",
        css: css`
            margin-bottom: 8px;
            display: block;
        `,
    },
    h6: {
        size: "14px",
        weight: fontConfig.weight.medium,
        lineHeight: "17px",
        css: css`
            margin-bottom: 8px;
            display: block;
        `,
    },
    headerPrimary: {
        forwardAs: "p",
        size: "24px",
        weight: fontConfig.weight.medium,
        fontFamily: fontConfig.fontFamily,
        color: "text.main",
        transform: "uppercase",
    },
    headerSecondary: {
        forwardAs: "p",
        size: "15px",
        weight: fontConfig.weight.medium,
        fontFamily: fontConfig.fontFamily,
        transform: "uppercase",
    },
    headerTertiary: {
        forwardAs: "p",
        size: "15px",
        fontFamily: fontConfig.fontFamily,
        color: "text.main",
        transform: "uppercase",
    },
    legend: {
        forwardAs: "span",
        size: "10px",
        color: "text.accent",
        transform: "uppercase",
        weight: "normal",
    },
} as xBaseTheme["typography"];
